import { onBeforeUnmount, onMounted } from 'vue'
import { useCookie, refreshCookie } from '#imports'
import { refreshedSessionCookie } from '~/utils/sessionCookie'
import useApi from '~/utils/apiUtils'

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
  'touchstart',
]

// Minimum gap between refresh calls. Keeps refresh traffic to roughly one
// request every 5 minutes during continuous activity.
const REFRESH_INTERVAL_MS = 5 * 60 * 1000

interface RefreshResponse {
  token: string
  idleSessionMinutes: number
}

let installed = false

export function useSessionRefresh() {
  if (typeof window === 'undefined') return
  if (installed) return
  installed = true

  const token = useCookie('authToken', { readonly: true })
  const api = useApi()

  let lastRefreshAt = Date.now()
  let pending = false

  const refresh = async () => {
    if (pending) return
    if (!token.value) return
    pending = true
    try {
      const res = await api.post('/auth/refresh', {})
      if (!res.ok) {
        // 401 is handled centrally by apiUtils → redirects to /login.
        return
      }
      const data = (await res.json()) as RefreshResponse
      if (data?.token) {
        document.cookie = refreshedSessionCookie(
          data.token,
          window.location.protocol === 'https:',
        )
        refreshCookie('authToken')
        lastRefreshAt = Date.now()
      }
    } catch (e) {
      console.warn('session refresh failed', e)
    } finally {
      pending = false
    }
  }

  const onActivity = () => {
    if (!token.value) return
    if (Date.now() - lastRefreshAt < REFRESH_INTERVAL_MS) return
    refresh()
  }

  onMounted(() => {
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true })
    }
  })

  onBeforeUnmount(() => {
    for (const evt of ACTIVITY_EVENTS) {
      window.removeEventListener(evt, onActivity)
    }
    installed = false
  })
}
